<template>
  <!-- Main modal -->
  <div
    tabindex="-1"
    :aria-hidden="!isVisible"
    class="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-z-50 tw-max-h-full tw-w-full tw-bg-[#000000]/50 tw-transition-all dark:tw-bg-[#000000]/80"
    :class="isVisible"
    :data-test-id="testId"
  >
    <div
      ref="target"
      class="tw-fixed tw-left-1/2 tw-top-1/2 tw-max-h-full tw-w-full tw--translate-x-1/2 tw--translate-y-1/2"
      :class="sizeClass"
    >
      <!-- Modal content -->
      <div
        class="tw-relative tw-rounded-lg tw-bg-white tw-shadow dark:tw-bg-light"
      >
        <!-- Modal header -->
        <div
          class="tw-flex tw-items-start tw-justify-between tw-pt-4 tw-px-4"
          :class="{
            'tw-border-b tw-border-dorian-gray-50 dark:tw-border-dorian-gray-100 tw-p-4':
              !props.hideHeader,
          }"
        >
          <slot name="header.title" v-if="!props.hideHeader">
            <cl-heading :size="'h4'"> {{ props.headerLabel }} </cl-heading>
          </slot>

          <button
            type="button"
            class="tw-bg-transparent tw-ml-auto tw-inline-flex tw-items-center tw-rounded-md tw-p-1.5 tw-text-sm tw-text-dorian-gray-500 tw-outline-none tw-outline-offset-0 tw-transition-all hover:tw-bg-dorian-gray-50 focus:tw-outline-4 focus:tw-outline-dorian-gray-200/20 active:tw-bg-dorian-gray-100 active:tw-text-dorian-gray-500"
            :data-test-id="`${props.testId}-close`"
            @click="emitClose"
          >
            <svg
              aria-hidden="true"
              class="tw-h-5 tw-w-5"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              ></path>
            </svg>
            <span class="tw-sr-only">Close modal</span>
          </button>
        </div>
        <!-- Modal body -->
        <div
          class="tw-max-h-[70vh] tw-space-y-6 tw-overflow-y-scroll tw-p-6"
          :class="{ 'tw-pt-0': props.hideHeader }"
        >
          <!-- Modal breadcrumbs -->
          <slot name="breadcrumbs"></slot>
          <slot></slot>
        </div>
        <!-- Modal footer -->
        <div
          v-if="!props.hideFooter"
          class="tw-flex tw-items-center tw-justify-end tw-space-x-2 tw-border-t tw-border-dorian-gray-50 tw-p-6 dark:tw-border-dorian-gray-100"
        >
          <slot name="footer.decline">
            <cl-button
              :data-test-id="`${props.testId}-${props.declineLabel}-button`"
              variant="link-secondary"
              @on-click="emitDecline"
            >
              {{ props.declineLabel }}
            </cl-button>
          </slot>
          <slot name="footer.accept">
            <cl-button
              :variant="'secondary'"
              :data-test-id="`${props.testId}-${props.acceptLabel}-button`"
              @on-click="emitAccept"
            >
              {{ props.acceptLabel }}
            </cl-button>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { onClickOutside } from "@vueuse/core";

import { ClHeading } from "@cl/components/heading";
import { ClButton } from "@cl/components/button";

import type { Size } from "./types";

export interface ModalProps {
  visible: boolean;
  testId?: string;
  headerLabel?: string;
  acceptLabel?: string;
  declineLabel?: string;
  size?: Size;
  closeOnClickOutside?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
}

const props = withDefaults(defineProps<ModalProps>(), {
  testId: "default-test-id",
  headerLabel: "Modal",
  acceptLabel: "Accept",
  declineLabel: "Decline",
  size: "md",
  closeOnClickOutside: false,
  hideHeader: false,
  hideFooter: false,
});

const target = ref(null);

onClickOutside(target, () => {
  if (props.closeOnClickOutside) {
    emitClose();
  }
});

const isVisible = computed(() => {
  if (props.visible) {
    return "tw-visible  tw-opacity-1";
  }

  return "tw-invisible  tw-opacity-0";
});

const sizes = {
  sm: "tw-max-w-1xl",
  md: "tw-max-w-2xl",
  lg: "tw-max-w-3xl",
  xl: "tw-max-w-4xl",
  xxl: "tw-max-w-5xl",
};

const sizeClass = computed(() => {
  return [sizes[props.size]];
});

const emit = defineEmits<{
  (event: "on-close"): void;
  (event: "on-accept"): void;
  (event: "on-decline"): void;
}>();

const emitClose = (): void => {
  emit("on-close");
};

const emitAccept = (): void => {
  emit("on-accept");
};

const emitDecline = (): void => {
  emit("on-decline");
};
</script>

<style scoped></style>
